import { useEffect, useState } from 'react';
import { Loader2, Sparkles, Shield, Info } from 'lucide-react';

interface ValidationProgressModalProps {
  visible: boolean;
  startTime?: number;
}

/**
 * Overlay-modal som vises under KI-validering.
 *
 * KI-validerings-endepunktet er ikke streamet — det er ett synkront kall som
 * internt gjør 3–5 KI-steg (oversettelse, norsk validering, engelsk validering,
 * ESCO-kvalitetssjekk, Bloom-analyse). Vi kan derfor ikke vise reell fremdrift
 * uten backend-endring; i stedet roterer vi mellom informative fase-tekster
 * som forklarer hva som skjer, og viser medgått tid.
 *
 * Modalen blokkerer all interaksjon med studieplanen under prosessen
 * (fixed inset-0 + backdrop-blur + z-50) slik at brukeren ikke kan endre
 * data mens KI-en jobber.
 */

const PHASE_MESSAGES = [
  { title: 'Lagrer utkast', detail: 'Sikrer at siste endringer er lagret før validering', minSec: 0 },
  {
    title: 'Genererer engelsk oversettelse',
    detail:
      'Kompetanseattester utstedes tospråklig (norsk + engelsk) for at kandidaten skal kunne bruke dem i internasjonale sammenhenger — jobbsøknad, videre utdanning eller anerkjennelse i utlandet. Dette følger EU Micro-credentials Framework og Europass-standarden. KI-en oversetter tittel, læringsutbytter og innhold.',
    minSec: 3
  },
  { title: 'Kvalitetsvurderer oversettelsen', detail: 'KI sammenligner norsk kildetekst med engelsk versjon for å sikre at oversettelsen ivaretar faglig presisjon og læringsutbyttenes mening', minSec: 12 },
  { title: 'Validerer norsk innhold', detail: 'Kontrollerer læringsutbytter, ECTS-samsvar, NKR-nivå, Blooms taksonomi', minSec: 20 },
  { title: 'Validerer engelsk versjon', detail: 'Samme kvalitetsvurdering på den engelske versjonen av studieplanen', minSec: 40 },
  { title: 'Sjekker ESCO-koblinger', detail: 'Vurderer om koblede ferdigheter er relevante for læringsutbyttene', minSec: 55 },
  { title: 'Sammenstiller resultatet', detail: 'Beregner totalscore og genererer forbedringsforslag', minSec: 75 },
];

export default function ValidationProgressModal({ visible, startTime }: ValidationProgressModalProps) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!visible) return;
    const start = startTime || Date.now();
    setElapsed(Math.floor((Date.now() - start) / 1000));
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 500);
    return () => clearInterval(interval);
  }, [visible, startTime]);

  if (!visible) return null;

  // Finn aktiv fase basert på medgått tid
  const activePhaseIdx = PHASE_MESSAGES.reduce((idx, phase, i) => {
    return elapsed >= phase.minSec ? i : idx;
  }, 0);
  const activePhase = PHASE_MESSAGES[activePhaseIdx];

  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  const elapsedText = minutes > 0
    ? `${minutes} min ${seconds} sek`
    : `${seconds} sek`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="validation-progress-title"
    >
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg mx-4 w-full">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-kf-primary-10 rounded-lg">
            <Sparkles className="text-kf-primary" size={24} />
          </div>
          <div>
            <h2 id="validation-progress-title" className="text-xl font-bold text-kf-dark">
              KI-validering pågår
            </h2>
            <p className="text-sm text-gray-500">
              Studieplanen er låst mens KI-en arbeider
            </p>
          </div>
        </div>

        {/* Fase-info */}
        <div className="bg-kf-light border border-kf-primary-40 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Loader2 className="text-kf-primary animate-spin flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <div className="font-semibold text-kf-dark mb-1">
                {activePhase.title}
              </div>
              <div className="text-sm text-gray-700">
                {activePhase.detail}
              </div>
            </div>
          </div>
        </div>

        {/* Fremdriftsbar */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Steg {activePhaseIdx + 1} av {PHASE_MESSAGES.length}</span>
            <span>{elapsedText}</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-kf-primary transition-all duration-500"
              style={{
                width: `${Math.min(100, ((activePhaseIdx + 1) / PHASE_MESSAGES.length) * 100)}%`
              }}
            />
          </div>
        </div>

        {/* Info-boks */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 flex items-start gap-2">
          <Info size={16} className="flex-shrink-0 mt-0.5 text-blue-600" />
          <div>
            <div className="font-medium mb-0.5">Ikke lukk denne siden</div>
            <div className="text-blue-800 text-xs">
              KI-valideringen tar typisk 30–90 sekunder. Hvis den ser ut til å henge
              lenger enn 3 minutter, kan du trygt laste siden på nytt — utkastet er lagret.
            </div>
          </div>
        </div>

        {/* Personvern-note */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-start gap-2 text-xs text-gray-500">
          <Shield size={14} className="flex-shrink-0 mt-0.5" />
          <span>
            Studieplaner inneholder kun faglig innhold (læringsutbytter, moduler, ECTS)
            — ingen personopplysninger sendes til KI-tjenesten.
          </span>
        </div>
      </div>
    </div>
  );
}
